import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as echarts from 'echarts';
import { debounce } from 'lodash-es';
import { useSelector } from 'umi';
import { EchartsCommonBox } from '@op/components/EChartCommon';
import useChartOptions from '@op/hooks/useChartOptions/useChartOptions';
import OpinionLineChartEvents from './OpinionLineChartEvents';
import type { ParamsTimeType } from '@op/types/common';
import type {
  IOpinionChartEventData,
  IOpinionChartEventSetting,
} from './hooks/types';
import { appendTopTipsEventSeries } from './hooks/utils/appendTopTipsEventSeries';
import { remapOpinionEventStateToChartXAxis } from './hooks/utils/remapOpinionEventTimelineToXAxis';
import { normalizeOpinionDateType } from './hooks/utils/getTimelineTimeFormat';
import {
  applySeriesXAxisIndex,
  buildDualEventXAxisList,
} from './hooks/utils/buildDualEventXAxis';
import { ensureOpinionLineChartLegend } from './hooks/utils/ensureOpinionLineChartLegend';
import { hasBubbleEventContent } from './hooks/utils/buildInitialChartEventSetting';
import OpinionEventDetailsPop from './OpinionEventDetailsPop';
import { OPINION_BAR_AND_LINE_CHART_ROOT_CLASS } from './constants';
import '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/index.scss';
import './OpinionStyledLineChart.scss';

const IGNORED_SERIES_NAMES = ['TopTipsEvent', 'Total'];

const chartShowEventTopSpace: Record<string, number> = {
  daily: 60,
  weekly: 110,
  monthly: 110,
  hourly: 30,
};

interface IOpinionStyledLineChartProps {
  lineOption: IRecord;
  height: number;
  chartType?: string;
  /** 轴数量或条件性 series 变化时替换结构项；默认保持既有合并行为。 */
  replaceSeriesOnUpdate?: boolean;
  enableEventAnnotation?: boolean;
  eventData?: IOpinionChartEventData;
  chartEventSetting?: IOpinionChartEventSetting;
  dateType?: string;
}

function getXAxisDataFromLineOption(lineOption?: IRecord | null): string[] {
  const xAxis = lineOption?.xAxis;
  if (Array.isArray(xAxis)) {
    return (xAxis[0]?.data || []).map((item: any) => String(item));
  }
  return (xAxis?.data || []).map((item: any) => String(item));
}

const OpinionStyledLineChart = forwardRef<
  echarts.ECharts | null,
  IOpinionStyledLineChartProps
>(
  (
    {
      lineOption,
      height,
      chartType = 'line',
      replaceSeriesOnUpdate = false,
      enableEventAnnotation = false,
      eventData,
      chartEventSetting,
      dateType = 'daily',
    },
    ref,
  ) => {
    const reactEchartsRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<echarts.ECharts | null>(null);
    const finishedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const eventLayerReadyRef = useRef(
      hasBubbleEventContent(chartEventSetting?.bubbleEvent),
    );
    const [myChart, setMyChart] = useState<echarts.ECharts | null>(null);
    const [eventLayoutRevision, setEventLayoutRevision] = useState(0);
    const [loadingEvent, setLoadingEvent] = useState(() => {
      if (!enableEventAnnotation) {
        return false;
      }

      return !hasBubbleEventContent(chartEventSetting?.bubbleEvent);
    });
    const chartRealType = chartType === 'stackedLine' ? 'line' : 'line';
    const normalizedDateType = normalizeOpinionDateType(dateType);
    const xAxisData = useMemo(
      () => getXAxisDataFromLineOption(lineOption),
      [lineOption],
    );

    const remappedEventState = useMemo(() => {
      if (!enableEventAnnotation || !eventData || !chartEventSetting) {
        return {
          eventData,
          chartEventSetting,
        };
      }

      return remapOpinionEventStateToChartXAxis(
        eventData,
        chartEventSetting,
        xAxisData,
        normalizedDateType,
      );
    }, [
      enableEventAnnotation,
      eventData,
      chartEventSetting,
      xAxisData,
      normalizedDateType,
    ]);

    const showEventAxis = !!remappedEventState.chartEventSetting?.showEventAxis;

    const sanitizedLineOption = useMemo(() => {
      const baseOption = enableEventAnnotation
        ? appendTopTipsEventSeries(lineOption) || lineOption
        : lineOption;

      const topTipsSeries = (baseOption?.series || []).find(
        (item: IRecord) => item?.name === 'TopTipsEvent',
      );
      const visibleSeries = (baseOption?.series || []).filter(
        (item: IRecord) =>
          item?.name && !IGNORED_SERIES_NAMES.includes(String(item.name)),
      );
      let series = topTipsSeries
        ? [...visibleSeries, topTipsSeries]
        : visibleSeries;

      const xAxis = buildDualEventXAxisList({
        lineOption: baseOption,
        xAxisData,
        chartRenderEventList:
          remappedEventState.eventData?.chartRenderEventList || {},
        showEventAxis,
      });

      series = applySeriesXAxisIndex(series, 1);

      let top = baseOption?.grid?.top ?? 20;
      const reserveEventBubbleTopSpace =
        enableEventAnnotation &&
        !!remappedEventState.chartEventSetting?.showEventBubbles;

      if (reserveEventBubbleTopSpace) {
        top +=
          chartShowEventTopSpace[normalizedDateType] ||
          chartShowEventTopSpace.daily;
      }

      const { legend, gridBottom } = ensureOpinionLineChartLegend({
        baseOption,
        visibleSeries,
      });

      const baseGrid = baseOption?.grid || {};

      return {
        ...baseOption,
        series,
        xAxis,
        legend,
        // 全局 / tooltip 不展示日期标签，仅由日期轴 axisPointer（白底盖住灰字）高亮
        axisPointer: {
          ...(baseOption?.axisPointer || {}),
          lineStyle: {
            type: 'solid',
            color: 'rgba(38, 40, 50, 0.25)',
            ...(baseOption?.axisPointer?.lineStyle || {}),
          },
          link: [{ xAxisIndex: [0, 1] }],
          label: { show: false },
        },
        tooltip: {
          ...(baseOption?.tooltip || {}),
          axisPointer: {
            ...(baseOption?.tooltip?.axisPointer || {}),
            label: {
              ...(baseOption?.tooltip?.axisPointer?.label || {}),
              show: false,
            },
          },
        },
        grid: {
          ...baseGrid,
          containLabel: true,
          top,
          bottom: gridBottom,
        },
      };
    }, [
      lineOption,
      enableEventAnnotation,
      remappedEventState.chartEventSetting,
      remappedEventState.eventData,
      normalizedDateType,
      xAxisData,
      showEventAxis,
    ]);

    const mergeOptions = useChartOptions({
      userOptions: sanitizedLineOption,
      type: chartRealType,
      // 排除 grid / xAxis / axisPointer，保留情报侧日期 margin 与 hover 样式
      excludeOptions: ['xAxis', 'grid', 'axisPointer'],
    });

    const menuExpand = useSelector(
      (state: IRecord) => state?.opinionMenu?.menuExpand,
    );

    useImperativeHandle(ref, () => chartInstanceRef.current);

    const eventLayoutSignature = useMemo(
      () =>
        [
          showEventAxis,
          remappedEventState.chartEventSetting?.showEventBubbles,
          normalizedDateType,
          xAxisData.length,
        ].join('|'),
      [
        showEventAxis,
        remappedEventState.chartEventSetting?.showEventBubbles,
        normalizedDateType,
        xAxisData.length,
      ],
    );

    useEffect(() => {
      if (!enableEventAnnotation || eventLayerReadyRef.current) {
        return;
      }

      setLoadingEvent(true);
    }, [enableEventAnnotation, eventLayoutSignature]);

    useEffect(() => {
      const container = reactEchartsRef.current;
      if (!container) {
        return undefined;
      }

      const chartInstance =
        echarts.getInstanceByDom(container) || echarts.init(container);
      chartInstanceRef.current = chartInstance;
      setMyChart(chartInstance);

      const scheduleEventLayerReady = (): void => {
        if (!enableEventAnnotation) {
          setLoadingEvent(false);
          return;
        }

        if (finishedTimerRef.current !== null) {
          clearTimeout(finishedTimerRef.current);
        }

        finishedTimerRef.current = setTimeout(() => {
          setLoadingEvent(false);
          eventLayerReadyRef.current = true;
        }, eventLayerReadyRef.current ? 0 : 200);
      };

      chartInstance.on('finished', scheduleEventLayerReady);

      const handleResize = debounce(() => {
        if (enableEventAnnotation && !eventLayerReadyRef.current) {
          setLoadingEvent(true);
        }
        chartInstance.resize();
        setEventLayoutRevision(revision => revision + 1);
      }, 500);

      window.addEventListener('resize', handleResize);

      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);

      const menuResizeTimer = setTimeout(() => {
        chartInstance.resize();
        setEventLayoutRevision(revision => revision + 1);
      }, 350);

      return () => {
        chartInstance.off('finished', scheduleEventLayerReady);
        if (finishedTimerRef.current !== null) {
          clearTimeout(finishedTimerRef.current);
        }
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
        clearTimeout(menuResizeTimer);
      };
    }, [enableEventAnnotation]);

    useEffect(() => {
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance) {
        return;
      }

      // 图表在轴数量或条件性 series 变化时，需要整体替换结构项以清理旧配置。
      chartInstance.setOption(mergeOptions, {
        replaceMerge: replaceSeriesOnUpdate
          ? ['xAxis', 'yAxis', 'series']
          : ['xAxis'],
      });

      if (!enableEventAnnotation) {
        setLoadingEvent(false);
      }
    }, [mergeOptions, enableEventAnnotation, replaceSeriesOnUpdate]);

    useEffect(() => {
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance) {
        return;
      }
      chartInstance.resize();
      setEventLayoutRevision(revision => revision + 1);
    }, [menuExpand]);

    const showEventLayer =
      enableEventAnnotation &&
      remappedEventState.chartEventSetting &&
      (remappedEventState.chartEventSetting.showEventBubbles ||
        remappedEventState.chartEventSetting.showEventAxis) &&
      myChart &&
      (!loadingEvent ||
        hasBubbleEventContent(
          remappedEventState.chartEventSetting.bubbleEvent,
        ));

    return (
      <div
        className={`${OPINION_BAR_AND_LINE_CHART_ROOT_CLASS} dbi-opinion-styled-line-chart`}
      >
        <div className="dbi-opinion-styled-line-chart__event-layer-wrap">
          <EchartsCommonBox height={height}>
            <div ref={reactEchartsRef} style={{ width: '100%', height }} />
          </EchartsCommonBox>

          {showEventLayer && (
            <>
              {remappedEventState.chartEventSetting.showEventBubbles && (
                <OpinionLineChartEvents
                  myChart={myChart}
                  layoutRevision={eventLayoutRevision}
                  eventList={
                    remappedEventState.chartEventSetting.bubbleEvent || {}
                  }
                  chartRenderEventList={
                    remappedEventState.eventData?.chartRenderEventList || {}
                  }
                  format={remappedEventState.eventData?.format || ''}
                  maxTimeValue={xAxisData[xAxisData.length - 1]}
                  showEventAxis={
                    !!remappedEventState.chartEventSetting.showEventAxis
                  }
                  dateType={normalizedDateType as ParamsTimeType}
                  xAxisData={xAxisData}
                  bottomSafeZone={30}
                  highPriorityEventDirection="end"
                />
              )}
            </>
          )}

          {enableEventAnnotation &&
            showEventAxis &&
            myChart &&
            (!loadingEvent ||
              hasBubbleEventContent(
                remappedEventState.chartEventSetting?.bubbleEvent,
              )) && (
              <OpinionEventDetailsPop
                myChart={myChart}
                dateType={normalizedDateType as ParamsTimeType}
                eventList={
                  remappedEventState.eventData?.chartRenderEventList || {}
                }
                xAxisData={xAxisData}
                showEventAxis={showEventAxis}
              />
            )}
        </div>
      </div>
    );
  },
);

OpinionStyledLineChart.displayName = 'OpinionStyledLineChart';

export default OpinionStyledLineChart;
